import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { BRAND_BLUE } from "../../shared/colors";

/**
 * LagBarChart — forecast_to_booking_lag evidence component.
 *
 * Boxed chart (like BandBarChart / ExpectedVsActual): card with a title +
 * InfoTip header, window/sessions on the right, and the bar chart below.
 * No left sidebar. Four bars in time order: 72h before · 0-24h · 24-48h · 48-72h.
 * The "before" bar is the baseline (grey). Caption beneath the chart.
 *
 * Spec references: §7 (forecast_to_booking_lag).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const CHART_TITLE = "Bookings After a Forecast Improvement";
const INFO_TIP_TEXT =
  "Bookings in each window around a forecast improvement. The grey bar is the baseline (72h before). The blue bars show how quickly bookings follow a better forecast.";

const LagBarChart = ({ panel }) => {
  const f = panel.facts || {};
  const events = Number(f.improvement_events) || 0;

  const chartData = [
    { label: "72h before", value: Number(f.bookings_72h_before) || 0, isBaseline: true },
    { label: "0–24h after", value: Number(f.bookings_0_24h_after) || 0, isBaseline: false },
    { label: "24–48h after", value: Number(f.bookings_24_48h_after) || 0, isBaseline: false },
    { label: "48–72h after", value: Number(f.bookings_48_72h_after) || 0, isBaseline: false },
  ];

  return (
    <div>
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      {/* ─── Card header: title + InfoTip (left), window/sessions (right) ── */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <span>{CHART_TITLE}</span>
          <InfoTip text={INFO_TIP_TEXT} />
        </h3>
        {f.window_days && f.sessions != null && (
          <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Last {f.window_days} days · {fmtNum(f.sessions)} visits
          </p>
        )}
      </div>

      {/* ─── Chart ─────────────────────────────────────────────────────── */}
      <div className="min-h-[240px]">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={45}
              allowDecimals={false}
            />
            <ThemedChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <ThemedTooltipBox title={label}>
                    <div>Bookings: {fmtNum(payload[0].value)}</div>
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={48}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.isBaseline ? "#9CA3AF" : BRAND_BLUE} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend — centered below the graph */}
      <div className="flex items-center justify-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 mt-3">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-gray-400" />
          Baseline (72h before)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />
          After improvement
        </span>
      </div>
    </div>

      {/* Insight — outside the graph box (panel.sentence suppressed at page level) */}
      {panel.sentence && (
        <div className="mt-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <p className="text-xs text-blue-800 dark:text-blue-300 leading-snug">
            <span className="font-semibold">Insight:</span> {panel.sentence}
          </p>
        </div>
      )}
    </div>
  );
};

export default LagBarChart;
